import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AppModule } from '../../app.module';
import { IncidentService } from '../../shared/incident.service';
import { Incident, IncidentLocation, IncidentTyre } from '../../models/incident';
import { Subscription } from 'rxjs';
import { SharedDataService } from '../../shared/shareddata.service';
import { Driver, Transporter, Vehicle } from '../../models/client';
import { TyreLocation, UserType } from '../../shared/enums';
import { NotificationService } from '../../shared/notification.service';
import { ImageQueueService, QueuedImage } from '../../shared/image-queue.service';
import { DocumentCategory } from '../../models/user';
import { DriverService } from '../../shared/driver.service';
import { ClientService } from '../../shared/client.service';
import { Globals } from '../../shared/functions';
import { ValidationBorderDirective } from '../../shared/validation-border.directive';
import { environment } from '../../../environments/environment';
import { IncidentChat } from '../../shared/incident-chat/incident-chat';
import { ChatSenderType } from '../../models/chat';
import { ChatService } from '../../shared/chat.service';
import { WebsocketsService } from '../../shared/websockets.service';
import { ChatAlertsService } from '../../shared/chat-alerts.service';

@Component({
  selector: 'app-log-new-breakdown',
  imports: [AppModule, ValidationBorderDirective, IncidentChat],
  providers: [ChatAlertsService],
  templateUrl: './log-new-breakdown.html',
  styleUrl: './log-new-breakdown.less'
})
export class LogNewBreakdown implements OnInit, OnDestroy {
  readonly chatSender = ChatSenderType.Fleet;
  public fleetChatAvailable = false;
  private incidentLoadVersion = 0;
  private loadedFormFields: Record<string, string> = {};
  private loadedIncidentFields: Record<string, string | undefined> = {};
  public incident: Incident = new Incident();
  public mapUrl: SafeResourceUrl | null = null;
  public isLoading: boolean = false;
  public isViewImagesModalVisible: boolean = false;
  public isSubmitting: boolean = false;
  public isAddingPhoto: boolean = false;
  public removingDocumentId: number | null = null;

  // Form fields
  public transporterName: string = '';
  public driverName: string = '';
  public driverCell: string = '';
  public vehicleReg: string = '';
  public tyreSize: string = ''; // Stores gencode
  public description: string = '';
  public tyreLocation = TyreLocation;
  public orderNumber: string = '';

  public imageCategories: DocumentCategory[] = [];
  public transporters: Transporter[] = [];
  public filteredTransporters: string[] = [];
  public pendingImages: QueuedImage[] = [];
  private queueInterval: any = null;
  private onlineHandler: (() => void) | null = null;
  private queueSubscriptions: Subscription[] = [];

  private routeSubscription: Subscription | null = null;
  private websocketSubscription: Subscription | null = null;
  public currentIncidentId: number = 0;

  constructor(
    private route: ActivatedRoute,
    private incidentService: IncidentService,
    private sanitizer: DomSanitizer,
    public sharedDataService: SharedDataService,
    private notification: NotificationService,
    public imageQueueService: ImageQueueService,
    private driverService: DriverService,
    private clientService: ClientService,
    private chatService: ChatService,
    private websockets: WebsocketsService,
    private chatAlerts: ChatAlertsService
  ) {}

  @HostListener('document:click')
  enableChatAlerts(): void {
    this.chatAlerts.enable();
  }

  ngOnInit(): void {
    this.loadDocumentCategories();
    this.loadTransporters();
    // Load metadata if not already loaded
    if (this.sharedDataService.tyreSizes.length === 0) {
      this.sharedDataService.loadTyreSizes();
    }
    
    this.routeSubscription = this.route.params.subscribe(params => {
      const incidentId = +params['incidentId'];
      this.websocketSubscription?.unsubscribe();
      this.websocketSubscription = null;
      this.disableFleetChat();
      this.currentIncidentId = incidentId;
      if (Number.isSafeInteger(incidentId) && incidentId > 0) {
        this.websocketSubscription = this.websockets.connectIncident(incidentId, UserType.operator).subscribe(msg => {
          const incident = this.websockets.getIncidentUpdate(msg, this.currentIncidentId);
          if (incident && this.incident.incidentId === this.currentIncidentId) {
            this.incidentLoadVersion++;
            this.applyIncident(incident, true);
            if (!this.sharedDataService.isLoadingMetaData.value) this.isLoading = false;
          }
          const chatUpdate = this.websockets.handleChatMessage(msg, this.chatSender, this.currentIncidentId);
          if (chatUpdate && this.fleetChatAvailable) this.chatAlerts.notify(chatUpdate);
        });
        this.loadIncident(incidentId);
      }
    });
  }

  ngOnDestroy(): void {
    this.websocketSubscription?.unsubscribe();
    this.incidentLoadVersion++;
    this.disableFleetChat();
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
    if (this.queueInterval){
      clearInterval(this.queueInterval);
      this.queueInterval = null;
    }
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }
    this.queueSubscriptions.forEach(subscription => {
      subscription.unsubscribe();
    });
    this.queueSubscriptions = [];
  }

  private loadDocumentCategories(): void {
    this.driverService.getDocumentCategories().subscribe({
      next: (data) => { this.imageCategories = data; },
      error: () => {}
    });
  }

  private loadTransporters(): void {
    this.clientService.getTransporters().subscribe({
      next: (data) => {
        this.transporters = data;
        this.filteredTransporters = data.map(t => t.transporterName);
      },
      error: () => {}
    });
  }

  public onTransporterInput(value: string): void {
    const upper = value.toUpperCase();
    this.transporterName = upper;
    this.filteredTransporters = this.transporters
      .map(t => t.transporterName)
      .filter(name => name.includes(upper));
    this.syncIncidentDraftFields();
  }

  public onRefresh(): void {
    if (this.currentIncidentId) {
      this.loadIncident(this.currentIncidentId);
    }
  }

  public onSubscribeToBreakdownUpdates(): void {
    const message = `Subscribe me for updates to breakdown ${this.incident.incidentNumber} at: ${this.incident.location?.descriptionShort ?? ''}`;
    const whatsappUrl = `https://wa.me/${environment.whatsappSubscriptionNumber.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  }

  private loadIncident(incidentId: number): void {
    const version = ++this.incidentLoadVersion;
    this.isLoading = true;
    
    
    // Fetch from server
    this.incidentService.getIncidentNoToken(incidentId, 0).subscribe({
      next: (incident) => {
        if (version !== this.incidentLoadVersion || incidentId !== this.currentIncidentId) return;
        this.applyIncident(incident);
        this.initImageQueue(incidentId);
        // Only set loading to false if metadata is also done loading
        if (!this.sharedDataService.isLoadingMetaData.value) {
          this.isLoading = false;
        }
      },
      error: (error) => {
        if (version !== this.incidentLoadVersion || incidentId !== this.currentIncidentId) return;
        console.error('Error loading incident:', error);
        this.isLoading = false;
      }
    });
    
  }

  public disableFleetChat(): void {
    this.fleetChatAvailable = false;
    this.chatService.setFleetAccess(this.currentIncidentId, false);
  }

  private applyIncident(incident: Incident, preserveDraft = false): void {
    const updated = Object.assign(new Incident(), preserveDraft ? this.incident : {}, incident);
    updated.tyrePositions = updated.tyrePositions?.map(tyre => ({ ...tyre, size: tyre.size || '' })) ?? [];
    // Compare with the last server values to preserve unsaved form selections.
    for (const field of ['tyrePositions', 'tyreBrands', 'location', 'callcentreOperatorId'] as const) {
      const serverValue = JSON.stringify(updated[field]);
      if (preserveDraft && this.incident.incidentId === incident.incidentId &&
          JSON.stringify(this.incident[field]) !== this.loadedIncidentFields[field]) {
        Object.assign(updated, { [field]: this.incident[field] });
      }
      this.loadedIncidentFields[field] = serverValue;
    }
    this.incident = updated;
    if (!preserveDraft || typeof incident.allowFleetChat === 'boolean') {
      this.fleetChatAvailable = incident.incidentId === this.currentIncidentId && incident.allowFleetChat === true;
      this.chatService.setFleetAccess(this.currentIncidentId, this.fleetChatAvailable);
    }
    this.populateFields(preserveDraft);
    const location = this.incident.location;
    this.mapUrl = location?.latitude != null && location?.longitude != null
      ? this.sanitizer.bypassSecurityTrustResourceUrl(
          `https://maps.google.com/maps?q=${location.latitude},${location.longitude}&z=15&output=embed`)
      : null;
  }

  private populateFields(preserveDraft = false): void {
    const fields = {
      driverName: this.incident.driver?.name || '',
      driverCell: this.incident.driver?.phoneNumber || '',
      vehicleReg: this.incident.fleetVehicle?.registrationNumber || '',
      tyreSize: this.incident.tyrePositions?.[0]?.size || '',
      description: this.incident.incidentDescription || '',
      transporterName: this.incident.transporterName || '',
      orderNumber: this.incident.orderNumber || ''
    };
    for (const field of Object.keys(fields) as (keyof typeof fields)[]) {
      if (!preserveDraft || this[field] === this.loadedFormFields[field]) this[field] = fields[field];
    }
    this.loadedFormFields = fields;
    this.syncIncidentDraftFields();
  }

  private syncIncidentDraftFields(): void {
    this.incident.driver = this.incident.driver || new Driver();
    this.incident.driver.name = this.driverName;
    this.incident.driver.phoneNumber = this.driverCell;

    this.incident.fleetVehicle = this.incident.fleetVehicle || new Vehicle();
    this.incident.fleetVehicle.registrationNumber = this.vehicleReg;

    this.incident.tyrePositions = this.incident.tyrePositions || [];
    if (this.incident.tyrePositions.length > 0) {
      this.incident.tyrePositions[0].size = this.tyreSize;
    }

    this.incident.incidentDescription = this.description;
    this.incident.transporterName = this.transporterName;
    this.incident.orderNumber = this.orderNumber;
  }

  public onLocationSelected(location: IncidentLocation): void {
    if (this.incident) {
      this.incident.location = location;
    }
  }

  public onDriverCellInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = Globals.Functions.normalizePhoneDigits(input.value);
    if (input.value !== digits) {
      input.value = digits;
    }
    this.driverCell = digits;
    this.syncIncidentDraftFields();
  }

  public onDriverCellBlur(): void {
    if (this.driverCell) {
      this.driverCell = Globals.Functions.normalizeSAPhoneNumber(this.driverCell);
      this.syncIncidentDraftFields();
    }
  }

  public onVehicleRegInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const upper = Globals.Functions.normalizeVehicleReg(input.value);
    if (input.value !== upper) {
      input.value = upper;
    }
    this.vehicleReg = upper;
    this.syncIncidentDraftFields();
  }

  public onTyreSizeChange(value: string): void {
    this.tyreSize = value;
    this.syncIncidentDraftFields();
  }

  public onDescriptionChange(value: string): void {
    this.description = value;
    this.syncIncidentDraftFields();
  }

  public onOrderNumberChange(value: string): void {
    this.orderNumber = value;
    this.syncIncidentDraftFields();
  }

  public onSelectTyreType(brandId: number): void {
    const existingIndex = this.incident.tyreBrands.findIndex(t => t === brandId);
    if (existingIndex !== -1) {
      this.incident.tyreBrands.splice(existingIndex, 1);
    } else {
      this.incident.tyreBrands.push(brandId);
    }
  }

  public isTyreTypeSelected(brandId: number): boolean {
    return this.incident.tyreBrands.some(t => t === brandId);
  }

  public hasSelectedTyreLocation(): boolean {
    return this.incident.tyrePositions?.some(t => t.tyreLocation > 0) ?? false;
  }

  public hasSelectedTyreType(): boolean {
    return (this.incident.tyreBrands?.length ?? 0) > 0;
  }

  public canSubmit(): boolean {
    return !!(this.driverCell);
  }

  public onSubmit(): void {
    this.isSubmitting = true;
    this.syncIncidentDraftFields();
    //get the callcentre clientid
    let ccId = 0;
    if (this.incident.incidentClients.filter(client => client.connectionTypeId === 2).length > 0) {
      ccId = this.incident.incidentClients.filter(client => client.connectionTypeId === 2)[0].clientId;
    }
    else if (this.incident.incidentClients.filter(client => client.connectionTypeId === 3).length > 0) {
      ccId = this.incident.incidentClients.filter(client => client.connectionTypeId === 3)[0].clientId;
    }
    //check if sps exist
    const spExist = this.incident.incidentClients.some(client => client.connectionTypeId === 3);
    if (this.tyreSize) {
      // Only add new item if there isn't an item, otherwise update existing
      if (this.incident.tyrePositions.length > 0) {
        this.incident.tyrePositions[0].size = this.tyreSize;
      } else {
        this.incident.tyrePositions.push(new IncidentTyre(TyreLocation.trailer, 0, this.tyreSize));
      }
    }
    if (this.incident && this.incident.location) {
      this.incident.location = this.incident.location;
    }
    console.log(this.incident);
    
    this.incidentService.updateIncidentNoToken(this.incident, ccId, spExist ? 0 : 5).subscribe({
      next: (updatedIncident) => {
        this.isSubmitting = false;
        this.notification.showSuccess('Breakdown updated successfully!');
        this.applyIncident(updatedIncident);
        console.log("Updated incident");
        console.log(this.incident);


      },
      error: (error) => {
        this.isSubmitting = false;
        this.notification.handleError(error, 'Error updating breakdown. Please try again.');
      }
    });
  }

  public onClickTyreLocation(tyreLocation: TyreLocation): void {
    // Single-select: only one location can be active at a time
    const existingTyres = this.incident!.tyrePositions.filter(t => t.tyreLocation === tyreLocation);
    if (existingTyres.length > 0) {
      // Same location already selected — do nothing, must always have one selected
      return;
    } else {
      // Different location — preserve size and qty from previous selection, then replace
      const prevTyre = this.incident!.tyrePositions[0];
      const preservedSize = prevTyre?.size;
      const preservedQty = prevTyre?.qty;
      const newTyre = new IncidentTyre(tyreLocation, 0);
      if (preservedSize || this.tyreSize) newTyre.size = preservedSize || this.tyreSize;
      if (preservedQty) newTyre.qty = preservedQty;
      this.incident!.tyrePositions = [newTyre];
      this.syncIncidentDraftFields();
    }
  }

  public get etaMinutesRemaining(): number {
    if (!this.incident?.breakdownTechnicianDriverEta) return 0;
    const eta = new Date((this.incident.breakdownTechnicianDriverEta as any) + 'Z');
    const now = new Date();
    return Math.max(0, Math.round((eta.getTime() - now.getTime()) / 60000));
  }

  public hasTyreInLocation(tyreLocation: TyreLocation): boolean {
      return this.incident!.tyrePositions.find(t => t.tyreLocation === tyreLocation) !== undefined;
    }
  
  private initImageQueue(incidentId: number): void {
    this.imageQueueService.resetStuckUploads(incidentId)
    .then(() => this.imageQueueService.getPendingImagesForIncident(incidentId))
    .then((pending) => {
      this.pendingImages = pending;
      this.imageQueueService.processQueue(incidentId);
    });

    this.queueInterval = setInterval(() => {
      this.imageQueueService.processQueueNoToken(incidentId);
    }, 30000); // Process every 30 seconds

    this.onlineHandler = () => {
      this.imageQueueService.processQueue(incidentId);
    };
    window.addEventListener('online', this.onlineHandler);

    // Subscribe to queue updates
    const sub = this.imageQueueService.uploadSuccess$.subscribe(({ localId, document }) => {
      const pending = this.pendingImages.find(img => img.id === localId);
      if (pending) {
        document.localBase64 = pending.fileBase64;
      }
      this.incident?.documents.push(document);
      this.pendingImages = this.pendingImages.filter(img => img.id !== localId);
    });

    const errorSub = this.imageQueueService.uploadFailed$.subscribe(({ localId }) => {
      const img = this.pendingImages.find(i => i.id === localId);
      if (img) img.status = 'failed';
    });

    this.queueSubscriptions.push(sub, errorSub);
  }

  private compressImageHalf(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width / 2);
          canvas.height = Math.round(img.height / 2);
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL('image/jpeg', 0.7));
          } else {
            reject(new Error('Failed to get canvas context'));
          }
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  public async onGallerySelect(event: Event, category?: DocumentCategory): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    if (!this.incident || !this.incident.incidentId) {
      this.notification.showError('Cannot upload images: No active breakdown');
      input.value = '';
      return;
    }
    this.isAddingPhoto = true;
    const files = Array.from(input.files);
    for (const file of files) {
      const compressedBase64 = await this.compressImageHalf(file);
      const localId = await this.imageQueueService.addImage({
        incidentId: this.incident.incidentId,
        fileBase64: compressedBase64,
        name: file.name,
        documentClassificationId: 1,
        subCategoryId: category?.categoryId ?? 5,
        createdByUserId: 0,
        dtCreated: new Date(),
        description: category?.categoryDescription || ''
      });
      this.pendingImages.push({
        id: localId,
        incidentId: this.incident.incidentId,
        fileBase64: compressedBase64,
        name: file.name,
        documentClassificationId: 1,
        subCategoryId: category?.categoryId ?? 5,
        createdByUserId: 0,
        dtCreated: new Date(),
        description: category?.categoryDescription || '',
        status: 'pending',
        retryCount: 0
      });
    }
    this.imageQueueService.processQueueNoToken(this.incident.incidentId);
    input.value = '';
    this.isAddingPhoto = false;
  }

  public async onCameraCapture(event: Event, category?: DocumentCategory): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    if (!this.incident || !this.incident.incidentId) {
      this.notification.showError('Cannot upload photo: No active breakdown');
      input.value = '';
      return;
    }
    this.isAddingPhoto = true;
    const file = input.files[0];
    const compressedBase64 = await this.compressImageHalf(file);
    const localId = await this.imageQueueService.addImage({
      incidentId: this.incident.incidentId,
      fileBase64: compressedBase64,
      name: file.name,
      documentClassificationId: 1,
      subCategoryId: category?.categoryId ?? 4,
      createdByUserId: 0,
      dtCreated: new Date(),
      description: category?.categoryDescription || ''
    });
    this.pendingImages.push({
      id: localId,
      incidentId: this.incident.incidentId,
      fileBase64: compressedBase64,
      name: file.name,
      documentClassificationId: 1,
      subCategoryId: category?.categoryId ?? 4,
      createdByUserId: 0,
      dtCreated: new Date(),
      description: category?.categoryDescription || '',
      status: 'pending',
      retryCount: 0
    });
    this.imageQueueService.processQueueNoToken(this.incident.incidentId);
    input.value = '';
    this.isAddingPhoto = false;
  }

  public hasPendingImages(): boolean {
    return this.pendingImages.length > 0;
  }

  public hasImages(): boolean {
    const hasUploaded = this.incident?.documents?.some(doc => doc.documentClassificationId === 1) ?? false;
    return hasUploaded || this.pendingImages.length > 0;
  }

  public getUploadedImages() {
    return this.incident?.documents?.filter(doc => doc.documentClassificationId === 1) ?? [];
  }

  public getImageCount(): number {
    const uploaded = this.incident?.documents?.filter(doc => doc.documentClassificationId === 1).length ?? 0;
    return uploaded + this.pendingImages.length;
  }

  public getCategoryImages(categoryId: number) {
    return this.getUploadedImages().filter(doc => doc.subCategoryId === categoryId);
  }

  public getPendingImagesByCategory(categoryId: number): QueuedImage[] {
    return this.pendingImages.filter(img => img.documentClassificationId === 1 && img.subCategoryId === categoryId);
  }

public onViewImages(): void {
  this.isViewImagesModalVisible = true;
}

public onRemovePendingImage(localId: number): void {
  this.imageQueueService.removeImage(localId).then(() => {
    this.pendingImages = this.pendingImages.filter(img => img.id !== localId);
  });
}

public onRemoveUploadedImage(documentId: number): void {
  if (!this.incident?.incidentId) return;
  this.removingDocumentId = documentId;
  this.incidentService.removeFileFromIncidentNoToken(documentId, this.incident.incidentId).subscribe({
    next: () => {
      this.incident!.documents = this.incident!.documents.filter(doc => doc.documentId !== documentId);
      this.removingDocumentId = null;
      this.notification.showSuccess('Image removed successfully');  
    },
    error: (error) => {
      this.removingDocumentId = null;
      this.notification.handleError(error, 'Failed to remove image');
    }
  });
}
}
